// 🔥 Firebase 디바이스 카탈로그 서비스 - 최고급 구현
// [배경] 상수 의존성을 제거하고 Firebase를 SSOT로 사용하는 핵심 서비스
//        성능 최적화(캐싱, 배치 처리), 에러 핸들링, 타입 안전성 모두 고려

import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  setDoc,
  updateDoc,
  deleteDoc,
  query, 
  where, 
  orderBy, 
  limit as firestoreLimit,
  writeBatch,
  serverTimestamp,
  QueryConstraint
} from 'firebase/firestore';
import { db } from '../firebase';
import { 
  DeviceCatalogDocument, 
  DeviceSearchOptions, 
  NewDeviceModelRequest,
  DeviceType,
  DeviceBrand,
  generateModelId,
  generateAliases
} from '../types/deviceCatalog';
import { AdvancedMemoization, BatchLoader, performanceMeasure } from '../utils/performanceOptimization';

// 🔄 캐시 관리 (성능 최적화)
class CacheManager {
  private static cache = new Map<string, { data: any; timestamp: number; ttl: number }>();
  
  static get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    if (!entry) return null;
    
    if (Date.now() - entry.timestamp > entry.ttl) {
      this.cache.delete(key);
      return null;
    }
    
    return entry.data as T;
  }
  
  static set<T>(key: string, data: T, ttl: number = 5 * 60 * 1000): void {
    this.cache.set(key, {
      data,
      timestamp: Date.now(),
      ttl
    });
  }
  
  static invalidate(pattern?: string): void {
    if (!pattern) {
      this.cache.clear();
      return;
    }
    
    Array.from(this.cache.keys())
      .filter(key => key.includes(pattern))
      .forEach(key => this.cache.delete(key));
  }
}

/**
 * 🔥 디바이스 카탈로그 서비스 (최고급 구현 + 성능 최적화)
 */
export class DeviceCatalogService {
  private static readonly COLLECTION_NAME = 'device_catalog';
  private static readonly DEFAULT_CACHE_TTL = 5 * 60 * 1000; // 5분
  private static readonly BATCH_SIZE = 400;
  
  // 🔥 배치 로더 인스턴스 (모델 ID 배치 조회용)
  private static modelBatchLoader = new BatchLoader<string, DeviceCatalogDocument | null>(
    async (modelIds: string[]) => {
      return performanceMeasure.measureTime(
        `배치 모델 조회 (${modelIds.length}개)`,
        async () => {
          const results: (DeviceCatalogDocument | null)[] = [];
          
          // Firestore 'in' 쿼리는 최대 10개까지만 지원하므로 청크로 분할
          const chunks = [];
          for (let i = 0; i < modelIds.length; i += 10) {
            chunks.push(modelIds.slice(i, i + 10));
          }
          
          for (const chunk of chunks) {
            const snapshot = await getDocs(query(
              collection(db, this.COLLECTION_NAME),
              where('__name__', 'in', chunk)
            ));
            
            const chunkResults = new Map<string, DeviceCatalogDocument>();
            snapshot.docs.forEach(doc => {
              chunkResults.set(doc.id, { id: doc.id, ...doc.data() } as DeviceCatalogDocument);
            });
            
            // 요청 순서대로 결과 배열 구성
            chunk.forEach(id => {
              results.push(chunkResults.get(id) || null);
            });
          }
          
          return results;
        }
      );
    },
    { maxBatchSize: 50, batchInterval: 20 }
  );
  
  // 📋 카테고리별 모델 목록 조회 (캐시 적용)
  static async getModelsByCategory(
    category: DeviceType, 
    options: { useCache?: boolean; orderBy?: 'releaseYear' | 'priority' } = {}
  ): Promise<DeviceCatalogDocument[]> {
    const { useCache = true, orderBy: orderField = 'releaseYear' } = options;
    const cacheKey = `category-${category}-${orderField}`;
    
    // 캐시 확인
    if (useCache) {
      const cached = CacheManager.get<DeviceCatalogDocument[]>(cacheKey);
      if (cached) return cached;
    }
    
    try {
      // 🔧 임시: 전체 컬렉션 조회 후 클라이언트 필터링 (인덱스 에러 방지)
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      
      let models = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DeviceCatalogDocument[];
      
      // 🔧 클라이언트 사이드 필터링 및 정렬 (관대한 필터링)
      const totalBeforeFilter = models.length;
      
      models = models.filter(m => {
        const categoryMatch = m.deviceType === category;
        const activeMatch = m.isActive !== false; // undefined, null, true 모두 활성으로 처리
        return categoryMatch && activeMatch;
      });
      
      // 📊 요약 로그만 출력 (개별 항목 로그 제거)
      console.log(`🔍 ${category} 카테고리 필터링: ${totalBeforeFilter}개 → ${models.length}개`);
      
      // 정렬 적용
      if (orderField === 'releaseYear') {
        models = models.sort((a, b) => (b.releaseYear || 0) - (a.releaseYear || 0));
      } else {
        models = models.sort((a, b) => (a.priority || 0) - (b.priority || 0));
      }
      
      // 🔥 중복 모델 제거 (동일한 modelKo 기준)
      const uniqueModels = this.deduplicateModels(models);
      const duplicateCount = models.length - uniqueModels.length;
      
      // 📊 최종 결과 로그 (중복 있을 때만 추가 표시)
      if (duplicateCount > 0) {
        console.log(`🔍 ${category}: 중복 ${duplicateCount}개 제거 → 최종 ${uniqueModels.length}개`);
      }
      
      // 캐시 저장
      if (useCache) {
        CacheManager.set(cacheKey, uniqueModels, this.DEFAULT_CACHE_TTL);
      }
      
      return uniqueModels;
    } catch (error) {
      console.error(`❌ 카테고리별 모델 조회 실패 (${category}):`, error);
      throw new Error(`카테고리 '${category}' 모델 조회 실패`);
    }
  }
  
  // 🏷️ 브랜드+카테고리별 모델 조회 (성능 최적화)
  static async getModelsByBrandAndCategory(
    brand: DeviceBrand, 
    category: DeviceType,
    options: { useCache?: boolean } = {}
  ): Promise<DeviceCatalogDocument[]> {
    const { useCache = true } = options;
    const cacheKey = `brand-category-${brand}-${category}`;
    
    // 캐시 확인
    if (useCache) {
      const cached = CacheManager.get<DeviceCatalogDocument[]>(cacheKey);
      if (cached) return cached;
    }
    
    try {
      // 🔧 임시: 가장 안전한 단순 쿼리 (인덱스 에러 방지)
      const snapshot = await getDocs(collection(db, this.COLLECTION_NAME));
      
      let models = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DeviceCatalogDocument[];
      
      // 🔧 클라이언트 사이드 필터링 및 정렬 (관대한 필터링)
      const totalBeforeFilter = models.length;
      
      models = models
        .filter(m => {
          const categoryMatch = m.deviceType === category;
          const brandMatch = m.brand === brand;
          const activeMatch = m.isActive !== false; // 관대한 활성화 필터
          return categoryMatch && brandMatch && activeMatch;
        })
        .sort((a, b) => {
          // 출시년도 내림차순 → 우선순위 오름차순
          if (a.releaseYear !== b.releaseYear) {
            return (b.releaseYear || 0) - (a.releaseYear || 0);
          }
          return (a.priority || 0) - (b.priority || 0);
        });
      
      // 🔥 중복 모델 제거 (동일한 modelKo 또는 id 기준)
      const uniqueModels = this.deduplicateModels(models);
      const duplicateCount = models.length - uniqueModels.length;
      
      // 📊 요약 로그만 출력 (개별 항목 로그 제거)
      console.log(
        `🔍 ${brand} ${category}: ${totalBeforeFilter}개 → 필터 ${models.length}개 → 중복제거 ${uniqueModels.length}개` +
        (duplicateCount > 0 ? ` (중복 ${duplicateCount}개 제거)` : '')
      );
      
      // 캐시 저장
      if (useCache) {
        CacheManager.set(cacheKey, uniqueModels, this.DEFAULT_CACHE_TTL);
      }
      
      return uniqueModels;
    } catch (error) {
      console.error(`❌ 브랜드+카테고리 모델 조회 실패 (${brand}, ${category}):`, error);
      throw new Error(`${brand} ${category} 모델 조회 실패`);
    }
  }
  
  // 🔥 중복 모델 제거 유틸리티
  // [배경] Firestore에 같은 모델이 여러 번 저장된 경우를 방어적으로 처리
  //        modelKo(한글 모델명)에서 용량 정보를 제외하고 중복 제거
  //        예: "갤럭시 S25 512GB"와 "갤럭시 S25 128GB"는 같은 모델로 간주
  //
  // 🔑 P2 핵심: S25 / S25+ / S25++ 는 절대 같은 모델로 병합하면 안 됨!
  //            + / ++ / plus 변형은 반드시 구분 토큰으로 보존해야 함
  private static deduplicateModels(models: DeviceCatalogDocument[]): DeviceCatalogDocument[] {
    const modelMap = new Map<string, DeviceCatalogDocument>();
    const duplicatesFound: { original: string; duplicate: string; key: string }[] = [];
    
    // 🔥 용량 정보를 제외한 순수 모델명 추출 헬퍼
    // 🔑 P2 핵심: + / ++ / plus 는 반드시 보존해야 함!
    const extractBaseModelName = (modelKo: string | undefined): string => {
      if (!modelKo) return '';
      
      // 🔑 P2 핵심: + / ++ 를 먼저 정규화 토큰으로 변환 (유실 방지!)
      // 순서 중요: ++ 먼저 처리, 그 다음 + 처리
      let normalized = modelKo
        .trim()
        // ++ → -plus-plus (공백 처리도 함께)
        .replace(/\s*\+\+\s*/g, '-plus-plus-')
        // + → -plus (공백 처리도 함께)
        .replace(/\s*\+\s*/g, '-plus-')
        // "플러스플러스" 한글도 -plus-plus로 정규화
        .replace(/플러스플러스/g, '-plus-plus-')
        // "플러스" 한글도 -plus로 정규화
        .replace(/플러스/g, '-plus-')
        // 소문자 변환
        .toLowerCase();
      
      // 용량 패턴 제거: 128GB, 256GB, 512GB, 1TB 등
      // 패턴: 숫자 + GB/TB (앞뒤 공백/하이픈 포함)
      normalized = normalized
        .replace(/[-\s]*\d+\s*(gb|tb)[-\s]*/gi, '')  // 용량 정보 제거
        .replace(/\s+/g, '')                         // 모든 공백 제거
        .replace(/-+/g, '-')                         // 연속 하이픈 정리
        .replace(/^-|-$/g, '')                       // 앞뒤 하이픈 제거
        .trim();
      
      return normalized;
    };
    
    // 🔍 P2 회귀 방지: S25 / S25+ / S25++ 변형 모델 구분 검증
    const plusVariantModels = models.filter(m => {
      const name = (m.modelKo || '').toLowerCase();
      return name.includes('+') || name.includes('플러스');
    });
    
    if (plusVariantModels.length > 0) {
      console.log('🔍 [P2 회귀 방지] + 변형 모델 구분 검증:');
      plusVariantModels.forEach(m => {
        const baseName = extractBaseModelName(m.modelKo);
        const originalName = m.modelKo || '';
        // ++ 또는 + 가 baseName에 올바르게 포함되어 있는지 확인
        const hasPlusPlus = originalName.includes('++') || originalName.includes('플러스플러스');
        const hasPlus = !hasPlusPlus && (originalName.includes('+') || originalName.includes('플러스'));
        const idHasPlusPlus = baseName.includes('plus-plus');
        const idHasPlus = !idHasPlusPlus && baseName.includes('plus');
        
        const isCorrect = 
          (hasPlusPlus && idHasPlusPlus) || 
          (hasPlus && idHasPlus) || 
          (!hasPlusPlus && !hasPlus && !baseName.includes('plus'));
        
        const icon = isCorrect ? '✅' : '🚨';
        console.log(`   ${icon} modelKo: "${m.modelKo}" → baseName: "${baseName}" | id: ${m.id}`);
        if (!isCorrect) {
          console.error(`   ⚠️ [P2 회귀 감지] + 변형이 올바르게 처리되지 않음!`);
        }
      });
    }
    
    // 🔍 디버깅: S25 관련 모델만 상세 로그 (용량 포함된 원본 분석)
    const s25Models = models.filter(m => m.modelKo?.toLowerCase().includes('s25'));
    if (s25Models.length > 0) {
      console.log('🔍 [중복제거 디버그] S25 관련 모델 분석:');
      s25Models.forEach(m => {
        const baseName = extractBaseModelName(m.modelKo);
        console.log(`   - modelKo: "${m.modelKo}" → baseName: "${baseName}" | id: ${m.id} | storage: [${m.storageOptions?.join(', ')}]`);
      });
    }
    
    models.forEach(model => {
      // 🔥 용량 정보를 제외한 순수 모델명으로 중복 체크
      const baseModelName = extractBaseModelName(model.modelKo);
      const key = baseModelName || model.id.toLowerCase();
      
      const existing = modelMap.get(key);
      if (!existing) {
        modelMap.set(key, model);
      } else {
        // 중복 발견 로그
        duplicatesFound.push({
          original: existing.modelKo || existing.id,
          duplicate: model.modelKo || model.id,
          key
        });
        
        // 중복인 경우: 더 최근에 업데이트된 것 또는 더 완전한 데이터 우선
        const existingUpdated = existing.updatedAt 
          ? (existing.updatedAt instanceof Date ? existing.updatedAt.getTime() : (existing.updatedAt as any).seconds * 1000)
          : 0;
        const newUpdated = model.updatedAt 
          ? (model.updatedAt instanceof Date ? model.updatedAt.getTime() : (model.updatedAt as any).seconds * 1000)
          : 0;
        
        // 🔥 중복 모델의 storageOptions 병합 (항상)
        const mergedStorageOptions = Array.from(new Set([
          ...(existing.storageOptions || []),
          ...(model.storageOptions || [])
        ]));
        
        // 더 최근에 업데이트된 것 우선
        if (newUpdated > existingUpdated || 
            (newUpdated === existingUpdated && (model.storageOptions?.length || 0) > (existing.storageOptions?.length || 0))) {
          modelMap.set(key, {
            ...model,
            storageOptions: mergedStorageOptions
          });
        } else {
          modelMap.set(key, {
            ...existing,
            storageOptions: mergedStorageOptions
          });
        }
      }
    });
    
    // 🔍 디버깅: 중복 제거 결과 요약
    if (duplicatesFound.length > 0) {
      console.log(`🔄 [중복제거] ${duplicatesFound.length}개 중복 발견 및 병합:`);
      duplicatesFound.slice(0, 10).forEach(d => {
        console.log(`   "${d.original}" ↔ "${d.duplicate}" (key: "${d.key}")`);
      });
      if (duplicatesFound.length > 10) {
        console.log(`   ... 외 ${duplicatesFound.length - 10}개`);
      }
    }
    
    return Array.from(modelMap.values());
  }
  
  // 🔍 시리즈별 모델 조회
  static async getModelsBySeries(series: string): Promise<DeviceCatalogDocument[]> {
    const cacheKey = `series-${series}`;
    const cached = CacheManager.get<DeviceCatalogDocument[]>(cacheKey);
    if (cached) return cached;
    
    try {
      const snapshot = await getDocs(query(
        collection(db, this.COLLECTION_NAME),
        where('series', '==', series),
        where('isActive', '==', true),
        orderBy('releaseYear', 'desc')
      ));
      
      const models = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DeviceCatalogDocument[];
      
      CacheManager.set(cacheKey, models, this.DEFAULT_CACHE_TTL);
      return models;
    } catch (error) {
      console.error(`❌ 시리즈별 모델 조회 실패 (${series}):`, error);
      throw new Error(`시리즈 '${series}' 모델 조회 실패`);
    }
  }
  
  // 🔍 고급 검색 (다중 조건, 성능 최적화)
  static async searchModels(searchOptions: DeviceSearchOptions): Promise<DeviceCatalogDocument[]> {
    try {
      const constraints: QueryConstraint[] = [
        where('isActive', '==', true)
      ];
      
      // 필터 조건 추가
      if (searchOptions.deviceType) {
        constraints.push(where('deviceType', '==', searchOptions.deviceType));
      }
      if (searchOptions.brand) {
        constraints.push(where('brand', '==', searchOptions.brand));
      }
      if (searchOptions.series) {
        constraints.push(where('series', '==', searchOptions.series));
      }
      if (searchOptions.recommended !== undefined) {
        constraints.push(where('recommended', '==', searchOptions.recommended));
      }
      
      // 정렬 조건
      const orderField = searchOptions.orderBy || 'releaseYear';
      const direction = searchOptions.orderDirection || 'desc';
      constraints.push(orderBy(orderField, direction));
      
      // 제한
      if (searchOptions.limit) {
        constraints.push(firestoreLimit(searchOptions.limit));
      }
      
      const snapshot = await getDocs(query(
        collection(db, this.COLLECTION_NAME),
        ...constraints
      ));
      
      let models = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      })) as DeviceCatalogDocument[];
      
      // 클라이언트 사이드 필터링 (Firestore 한계 보완)
      if (searchOptions.searchQuery) {
        const q = searchOptions.searchQuery.toLowerCase();
        models = models.filter(model =>
          model.modelKo.toLowerCase().includes(q) ||
          model.modelEn.toLowerCase().includes(q) ||
          model.aliases.some(alias => alias.includes(q))
        );
      }
      
      if (searchOptions.releaseYearFrom) {
        models = models.filter(m => m.releaseYear >= searchOptions.releaseYearFrom!);
      }
      if (searchOptions.releaseYearTo) {
        models = models.filter(m => m.releaseYear <= searchOptions.releaseYearTo!);
      }
      
      return models;
    } catch (error) {
      console.error('❌ 모델 검색 실패:', error);
      throw new Error('모델 검색 중 오류 발생');
    }
  }
  
  // 📱 특정 모델 상세 조회 (배치 로딩 최적화)
  static async getModelById(modelId: string): Promise<DeviceCatalogDocument | null> {
    const cacheKey = `model-${modelId}`;
    const cached = CacheManager.get<DeviceCatalogDocument>(cacheKey);
    if (cached) return cached;
    
    try {
      // 배치 로더를 통한 최적화된 조회
      const model = await this.modelBatchLoader.load(modelId);
      
      if (model) {
        CacheManager.set(cacheKey, model, this.DEFAULT_CACHE_TTL);
      }
      
      return model;
    } catch (error) {
      console.error(`❌ 모델 상세 조회 실패 (${modelId}):`, error);
      throw new Error(`모델 '${modelId}' 조회 실패`);
    }
  }

  // 🔥 여러 모델 동시 조회 (배치 최적화)
  static async getModelsByIds(modelIds: string[]): Promise<(DeviceCatalogDocument | null)[]> {
    return performanceMeasure.measureTime(
      `다중 모델 조회 (${modelIds.length}개)`,
      async () => {
        const promises = modelIds.map(id => this.getModelById(id));
        return Promise.all(promises);
      }
    );
  }
  
  // 🛠️ 새 모델 추가 (관리자용)
  static async addNewModel(modelData: NewDeviceModelRequest, adminId: string): Promise<string> {
    try {
      const modelId = generateModelId({
        brand: modelData.brand,
        deviceType: modelData.deviceType,
        series: modelData.series,
        modelEn: modelData.modelEn
      });
      
      const catalogDoc: DeviceCatalogDocument = {
        id: modelId,
        deviceType: modelData.deviceType,
        brand: modelData.brand,
        series: modelData.series,
        modelEn: modelData.modelEn,
        modelKo: modelData.modelKo,
        storageOptions: modelData.storageOptions,
        connectivityOptions: modelData.connectivityOptions || [],
        colorOptions: [],
        image: modelData.image,
        recommended: modelData.recommended || false,
        subcategory: modelData.series,
        releaseYear: modelData.releaseYear || new Date().getFullYear(),
        aliases: generateAliases({
          modelEn: modelData.modelEn,
          modelKo: modelData.modelKo,
          series: modelData.series
        }),
        isActive: true,
        priority: 0,
        createdAt: new Date(),
        updatedAt: new Date(),
        updatedBy: adminId
      };
      
      await setDoc(doc(db, this.COLLECTION_NAME, modelId), catalogDoc);
      
      // 관련 캐시 무효화
      CacheManager.invalidate(modelData.deviceType);
      CacheManager.invalidate(modelData.brand);
      
      console.log(`✅ 새 모델 추가 완료: ${modelId}`);
      return modelId;
    } catch (error) {
      console.error('❌ 새 모델 추가 실패:', error);
      throw new Error('새 모델 추가 중 오류 발생');
    }
  }
  
  // 🔄 모델 업데이트 (관리자용)
  static async updateModel(
    modelId: string, 
    updates: Partial<DeviceCatalogDocument>, 
    adminId: string
  ): Promise<void> {
    try {
      const updateData = {
        ...updates,
        updatedAt: new Date(),
        updatedBy: adminId
      };
      
      await updateDoc(doc(db, this.COLLECTION_NAME, modelId), updateData);
      
      // 캐시 무효화
      CacheManager.invalidate(modelId);
      
      console.log(`✅ 모델 업데이트 완료: ${modelId}`);
    } catch (error) {
      console.error(`❌ 모델 업데이트 실패 (${modelId}):`, error);
      throw new Error(`모델 '${modelId}' 업데이트 실패`);
    }
  }
  
  // 🗑️ 모델 비활성화 (소프트 삭제)
  static async deactivateModel(modelId: string, adminId: string): Promise<void> {
    try {
      await updateDoc(doc(db, this.COLLECTION_NAME, modelId), {
        isActive: false,
        updatedAt: new Date(),
        updatedBy: adminId,
        memo: `모델 비활성화 - ${new Date().toISOString()}`
      });
      
      // 캐시 무효화
      CacheManager.invalidate(modelId);
      
      console.log(`✅ 모델 비활성화 완료: ${modelId}`);
    } catch (error) {
      console.error(`❌ 모델 비활성화 실패 (${modelId}):`, error);
      throw new Error(`모델 '${modelId}' 비활성화 실패`);
    }
  }

  // 🔥 모델 완전 삭제 (물리적 삭제)
  static async deleteModelPermanently(modelId: string, adminId: string): Promise<void> {
    try {
      console.log(`🔥 모델 물리적 삭제 시작: ${modelId}`);
      
      // 1단계: 삭제 전 백업 정보 로그
      const modelDoc = await getDoc(doc(db, this.COLLECTION_NAME, modelId));
      if (modelDoc.exists()) {
        const modelData = modelDoc.data();
        console.log('🗃️ 삭제 전 백업 로그:', {
          id: modelId,
          modelKo: modelData?.modelKo,
          series: modelData?.series,
          deletedAt: new Date().toISOString(),
          deletedBy: adminId
        });
      }
      
      // 2단계: Firebase에서 완전 삭제
      await deleteDoc(doc(db, this.COLLECTION_NAME, modelId));
      
      // 3단계: 캐시 무효화
      CacheManager.invalidate(modelId);
      CacheManager.invalidate(); // 전체 캐시도 무효화
      
      console.log(`✅ 모델 물리적 삭제 완료: ${modelId}`);
      
    } catch (error) {
      console.error(`❌ 모델 물리적 삭제 실패 (${modelId}):`, error);
      throw new Error(`모델 '${modelId}' 물리적 삭제 실패: ${error instanceof Error ? error.message : String(error)}`);
    }
  }
  
  // 📊 통계 조회 (메모이제이션 적용)
  static getStatistics = AdvancedMemoization.memoize(
    async (): Promise<{
      totalModels: number;
      byCategory: Record<DeviceType, number>;
      byBrand: Record<DeviceBrand, number>;
      recommendedCount: number;
      lastUpdated: Date;
    }> => {
      return performanceMeasure.measureTime('통계 조회', async () => {
        const snapshot = await getDocs(query(
          collection(db, this.COLLECTION_NAME),
          where('isActive', '==', true)
        ));
        
        const models = snapshot.docs.map(doc => doc.data() as DeviceCatalogDocument);
        
        const stats = {
          totalModels: models.length,
          byCategory: {
            smartphone: models.filter(m => m.deviceType === 'smartphone').length,
            tablet: models.filter(m => m.deviceType === 'tablet').length,
            wearable: models.filter(m => m.deviceType === 'wearable').length
          } as Record<DeviceType, number>,
          byBrand: {
            Apple: models.filter(m => m.brand === 'Apple').length,
            Samsung: models.filter(m => m.brand === 'Samsung').length
          } as Record<DeviceBrand, number>,
          recommendedCount: models.filter(m => m.recommended).length,
          lastUpdated: new Date()
        };
        
        return stats;
      });
    },
    { 
      keyGenerator: () => 'catalog-stats',
      ttl: 2 * 60 * 1000 // 2분 캐시
    }
  );
  
  // 🔄 배치 업데이트 (관리자용 - 대량 작업)
  static async batchUpdateModels(
    updates: Array<{ modelId: string; data: Partial<DeviceCatalogDocument> }>,
    adminId: string
  ): Promise<{ success: number; failed: Array<{ modelId: string; error: string }> }> {
    const results = { success: 0, failed: [] as Array<{ modelId: string; error: string }> };
    
    try {
      // 배치 크기로 나누어 처리
      for (let i = 0; i < updates.length; i += this.BATCH_SIZE) {
        const batch = writeBatch(db);
        const chunk = updates.slice(i, i + this.BATCH_SIZE);
        
        chunk.forEach(({ modelId, data }) => {
          const docRef = doc(db, this.COLLECTION_NAME, modelId);
          batch.update(docRef, {
            ...data,
            updatedAt: serverTimestamp(),
            updatedBy: adminId
          });
        });
        
        await batch.commit();
        results.success += chunk.length;
        
        console.log(`✅ 배치 업데이트 진행: ${results.success}/${updates.length}`);
      }
      
      // 캐시 전체 무효화
      CacheManager.invalidate();
      
    } catch (error) {
      console.error('❌ 배치 업데이트 실패:', error);
      results.failed.push({ modelId: 'batch-error', error: String(error) });
    }
    
    return results;
  }
  
  // 🔄 캐시 관리 유틸리티
  static clearCache(): void {
    CacheManager.invalidate();
  }
  
  static getCacheStats(): { size: number; keys: string[] } {
    return {
      size: CacheManager['cache'].size,
      keys: Array.from(CacheManager['cache'].keys())
    };
  }
}
